import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Home, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import rooferImage from "@/assets/real-roofer.jpg";

export const RoofingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Roofing Services - Expert Roofers | Sortable"
        description="Find qualified roofers near you. Roof repairs, replacements, guttering, and emergency roof work. Fully insured roofing contractors across the UK. Free quotes available."
        keywords="roofer, roofing services, roof repair, roof replacement, emergency roof repair, guttering, roof tiles, slate roof, flat roof repair, roof maintenance, chimney repair, fascia and soffit, roof inspection, storm damage repair, roof leak repair, new roof installation, roof renovation, commercial roofing, residential roofing, local roofer"
        canonical="https://sortable.lovable.app/services/roofing"
        ogImage="https://sortable.lovable.app/real-roofer.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Roofing Services",
          "description": "Professional roofing and roof repair services",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Roofing Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-slate-600/10 border border-slate-600/20 text-slate-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Home className="h-5 w-5" />
            Professional Roofing Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Roofers & Roofing Contractors
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From emergency roof repairs to complete roof replacements, find qualified, insured roofers for all your roofing needs. Storm damage repairs and maintenance services available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Roofers Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Home className="mr-2 h-5 w-5" />
                Join as a Roofer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Roofing Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of qualified roofers provides comprehensive roofing solutions for residential and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Emergency Roof Repairs",
                description: "24/7 emergency roof repair services for leaks and storm damage",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Roof Replacement",
                description: "Complete roof replacement with modern materials and warranties",
                icon: <Home className="w-6 h-6" />
              },
              {
                title: "Roof Maintenance",
                description: "Regular roof inspections and preventive maintenance services",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Guttering Services",
                description: "Gutter cleaning, repair, and replacement including downpipes",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Flat Roof Specialists",
                description: "Flat roof installation, repair, and waterproofing solutions",
                icon: <Home className="w-6 h-6" />
              },
              {
                title: "Chimney & Flashing",
                description: "Chimney repairs, repointing, and flashing installation",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-600/10 rounded-lg text-slate-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Roofing Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our roofers are experienced professionals committed to quality workmanship and customer satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Fully Licensed & Insured</h4>
                    <p className="text-muted-foreground">
                      All roofers carry comprehensive public liability insurance and relevant certifications.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Quality Materials</h4>
                    <p className="text-muted-foreground">
                      Only premium roofing materials from trusted manufacturers with extended warranties.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Emergency Response</h4>
                    <p className="text-muted-foreground">
                      24/7 emergency roof repair service for urgent leaks and storm damage.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Workmanship Guarantee</h4>
                    <p className="text-muted-foreground">
                      Extended warranties on all roofing work with ongoing support and maintenance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={rooferImage} 
                alt="Professional roofer working on roof installation"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Roofer Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your roofing job and get quotes from qualified, local roofers. Emergency repairs, maintenance, and installations available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with qualified roofers across the UK for all your roofing needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Emergency Repairs</li>
                <li>Roof Replacement</li>
                <li>Roof Maintenance</li>
                <li>Guttering Services</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Roofers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};